import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, Button, Card, ActiveToggle, AvatarImage, ConfirmDialog, Field, InfoTip, PillButton, useBrand, brandDisplayName, apiErrorMessage } from '@aralere/shared-ui';
import type { Package, Service, Business, CreatePackageRequest, PackageActivePreflightResponse } from '@aralere/shared-types';
import { useListControls } from './useListControls';

const num = (s: string): number => {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};
const money = (n: number): string => `$${n.toFixed(2)}`;

type FormState = {
  id: string;
  name: string;
  shortName: string;
  clientFacingName: string;
  description: string;
  logoUrl: string;
  category: string;
  subCategory: string;
  defaultPrice: string;
  currency: string;
  defaultBillingEntity: string;
  sortOrder: string;
  serviceIds: string[];
};

function emptyForm(billingEntity: string): FormState {
  return {
    id: '',
    name: '',
    shortName: '',
    clientFacingName: '',
    description: '',
    logoUrl: '',
    category: '',
    subCategory: '',
    defaultPrice: '',
    currency: 'USD',
    defaultBillingEntity: billingEntity,
    sortOrder: '0',
    serviceIds: [],
  };
}

function formFromPackage(p: Package): FormState {
  return {
    id: p.id,
    name: p.name,
    shortName: p.shortName,
    clientFacingName: p.clientFacingName ?? '',
    description: p.description,
    logoUrl: p.logoUrl,
    category: p.category,
    subCategory: p.subCategory,
    // Show the current price; clearing it switches the package to margin pricing (off summed member cost).
    defaultPrice: String(p.defaultPrice),
    currency: p.currency,
    defaultBillingEntity: p.defaultBillingEntity,
    sortOrder: String(p.sortOrder),
    serviceIds: [...p.serviceIds],
  };
}

function buildPayload(f: FormState): CreatePackageRequest {
  const priceStr = f.defaultPrice.trim();
  return {
    id: f.id.trim() || undefined,
    name: f.name.trim(),
    shortName: f.shortName.trim(),
    // Blank → null; clients then see the full `name`.
    clientFacingName: f.clientFacingName.trim() || null,
    description: f.description.trim(),
    logoUrl: f.logoUrl.trim(),
    category: f.category.trim(),
    subCategory: f.subCategory.trim(),
    // Immutable after creation — the server ignores this on PATCH (#10), but it's sent for the create path.
    serviceIds: f.serviceIds,
    // Blank → null → server auto-prices from margin off the SUMMED member COSTS (spec §5.8/§5.9).
    defaultPrice: priceStr === '' ? null : num(priceStr),
    currency: f.currency.trim() || 'USD',
    defaultBillingEntity: f.defaultBillingEntity.trim(),
    sortOrder: num(f.sortOrder),
  };
}

/**
 * Service-package catalog admin (Stage 2, spec §5.9). Mirrors the Services catalog screen but leaner —
 * a package has no SSO / provisioning / SLA / retainer of its own; it bundles N catalog services under
 * one settable client price. The member set is chosen at create and IMMUTABLE after (#10): to change a
 * bundle's composition, create a new package and switch clients to it. Pricing mirrors services — a
 * number is manual; blank auto-prices from the billing entity's margin off the SUMMED member costs.
 */
export function AdminPackages(): JSX.Element {
  const { brand } = useBrand();
  const defaultBillingEntity = brand?.id ?? 'biz-aralere';

  const [packages, setPackages] = useState<Package[] | null>(null);
  const [assignedCounts, setAssignedCounts] = useState<Record<string, number>>({});
  const [services, setServices] = useState<Service[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm(defaultBillingEntity));
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // 04 #10 parity: the edited package's UPLOADED logo (live server state; upload/remove are
  // IMMEDIATE actions, not staged behind Save — the upload needs a package id). logoVersion
  // cache-busts the preview; logoError renders inside the block (not the out-of-view form error).
  const [editLogoRef, setEditLogoRef] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoError, setLogoError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [pkg, svc, biz] = await Promise.all([
        api.admin.listPackages(),
        api.admin.listServices(),
        api.admin.listBusinesses(),
      ]);
      setPackages(pkg.packages);
      setAssignedCounts(pkg.assignedClientCounts);
      setServices(svc.services);
      setBusinesses(biz.businesses);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (formOpen) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [formOpen]);

  const update = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));
  const toggleMember = (id: string): void =>
    setForm((f) => ({
      ...f,
      serviceIds: f.serviceIds.includes(id) ? f.serviceIds.filter((x) => x !== id) : [...f.serviceIds, id],
    }));

  const openCreate = (): void => {
    setEditingId(null);
    setForm(emptyForm(defaultBillingEntity));
    setEditLogoRef(null);
    setLogoError(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (p: Package): void => {
    setEditingId(p.id);
    setForm(formFromPackage(p));
    setEditLogoRef(p.logoBlobRef ?? null);
    setLogoError(null);
    setFormError(null);
    setFormOpen(true);
  };

  // 04 #10 parity: package-logo image upload — IMMEDIATE actions on the edited package (mirrors the
  // service lane). The uploaded image takes precedence over the URL; the URL field is never touched.
  const onPackageLogoFile = async (file: File | null): Promise<void> => {
    if (!file || !editingId) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const res = await api.admin.uploadPackageLogo(editingId, file);
      setEditLogoRef(res.logoBlobRef);
      setLogoVersion((v) => v + 1);
      await load();
    } catch (err) {
      setLogoError(apiErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  const removeUploadedLogo = async (): Promise<void> => {
    if (!editingId) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      await api.admin.removePackageLogo(editingId);
      setEditLogoRef(null);
      setLogoVersion((v) => v + 1);
      await load();
    } catch (err) {
      setLogoError(apiErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const servicesById = new Map(services.map((s) => [s.id, s]));
  const serviceName = (id: string): string => servicesById.get(id)?.name ?? id;
  const activeServices = services.filter((s) => s.active);
  const selectedCostSum = form.serviceIds.reduce((sum, id) => sum + (servicesById.get(id)?.defaultCostToYou ?? 0), 0);

  const selectedBiz = businesses.find((b) => b.id === form.defaultBillingEntity);
  const marginOnForEntity = !!selectedBiz?.standardMargin.enabled;

  const billingOptions: ReadonlyArray<readonly [string, string]> = businesses.length
    ? businesses.map((b) => [b.id, brandDisplayName(b.name)] as const)
    : [[form.defaultBillingEntity, form.defaultBillingEntity] as const];

  // Managed combobox values (mirrors Services): the distinct category / sub-category values already in use.
  const categoryOptions = Array.from(new Set((packages ?? []).map((p) => p.category).filter(Boolean))).sort();
  const subCategoryOptions = Array.from(new Set((packages ?? []).map((p) => p.subCategory).filter(Boolean))).sort();

  // ── list filters + pagination + multi-select ──
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const q = query.trim().toLowerCase();
  const filtered = (packages ?? []).filter((p) => {
    if (q && !`${p.name} ${p.shortName} ${p.clientFacingName ?? ''} ${p.id}`.toLowerCase().includes(q)) return false;
    if (catFilter && p.category !== catFilter) return false;
    if (subFilter && p.subCategory !== subFilter) return false;
    if (statusFilter === 'active' && !p.active) return false;
    if (statusFilter === 'inactive' && p.active) return false;
    return true;
  });
  const { page, setPage, totalPages, pageItems, selected, isSelected, toggle, setMany, clear } = useListControls(filtered, 25);
  const pageIds = pageItems.map((p) => p.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => isSelected(id));

  // Cascading filter options: each dropdown lists only values still reachable given the OTHER
  // dropdown's selection, so picking a category narrows the sub-category list and vice-versa.
  const filterCatOptions = Array.from(
    new Set((packages ?? []).filter((p) => !subFilter || p.subCategory === subFilter).map((p) => p.category).filter(Boolean)),
  ).sort();
  const filterSubOptions = Array.from(
    new Set((packages ?? []).filter((p) => !catFilter || p.category === catFilter).map((p) => p.subCategory).filter(Boolean)),
  ).sort();

  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkSetActive = async (next: boolean): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setError(null);
    setInfo(null);
    try {
      const pres = await Promise.all(ids.map((id) => api.admin.getPackageActivePreflight(id)));
      if (!next) {
        setCleanSlate(false);
        setConfirmState({ kind: 'disable', ids, pre: pres, fromBulk: true });
      } else if (pres.some((p) => p.pendingBatch)) {
        setCleanSlate(false);
        setConfirmState({ kind: 'enable', ids, pre: pres, fromBulk: true });
      } else {
        for (const id of ids) await api.admin.setPackageActive(id, true);
        clear();
        await load();
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    if (!editingId && form.serviceIds.length === 0) {
      setFormError('Select at least one service to include in the package.');
      return;
    }
    if (!marginOnForEntity && form.defaultPrice.trim() === '') {
      setFormError(
        `Enter a client price${selectedBiz ? ` for ${brandDisplayName(selectedBiz.name)}` : ''}. Its margin calculator is off, so the price can't be auto-filled. Enter 0 for a free package, or turn margin on in Settings.`,
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload(form);
      if (!payload.name || !payload.shortName) {
        throw new Error('Name and short name are required.');
      }
      if (editingId) {
        // PATCH uses the path id; the server ignores body.id + body.serviceIds (immutable), so the full payload is fine.
        await api.admin.updatePackage(editingId, payload);
      } else {
        await api.admin.createPackage(payload);
      }
      await load();
      closeForm();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── s30 item 4 (08 Q29): the Active toggle is a BULK CASCADE over the clientPackage + its child
  //    services — preflight first, then an in-app confirm (counts on disable; the restore-vs-clean-
  //    slate prompt on re-enable when suspended holders are pending). Replaces window.confirm.
  const [info, setInfo] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    kind: 'disable' | 'enable';
    ids: string[];
    pre: PackageActivePreflightResponse[];
    fromBulk: boolean;
  } | null>(null);
  const [cleanSlate, setCleanSlate] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const toggleActive = async (p: Package): Promise<void> => {
    setBusyId(p.id);
    setError(null);
    setInfo(null);
    try {
      const pre = await api.admin.getPackageActivePreflight(p.id);
      if (p.active) {
        setCleanSlate(false);
        setConfirmState({ kind: 'disable', ids: [p.id], pre: [pre], fromBulk: false });
      } else if (pre.pendingBatch) {
        setCleanSlate(false);
        setConfirmState({ kind: 'enable', ids: [p.id], pre: [pre], fromBulk: false });
      } else {
        await api.admin.setPackageActive(p.id, true);
        await load();
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const runConfirmed = async (): Promise<void> => {
    if (!confirmState) return;
    setConfirmBusy(true);
    setError(null);
    try {
      let clients = 0;
      let failures = 0;
      // 04 #116 / #117: the batch size is NOT the restored count and NOT the suspended-and-notified
      // count. Carry the server's own split figures rather than re-deriving one number for all three.
      let alreadySuspended = 0;
      let restored = 0;
      let heldByDebt = 0;
      for (const id of confirmState.ids) {
        const res =
          confirmState.kind === 'disable'
            ? await api.admin.setPackageActive(id, false)
            : await api.admin.setPackageActive(id, true, cleanSlate ? 'clean-slate' : 'restore-all');
        clients += res.bulk?.affectedClientCount ?? 0;
        failures += res.bulk?.failedRowCount ?? 0;
        alreadySuspended += res.bulk?.alreadySuspendedRowCount ?? 0;
        restored += res.bulk?.restoredClientCount ?? res.bulk?.affectedClientCount ?? 0;
        heldByDebt += res.bulk?.heldByDebtClientCount ?? 0;
      }
      const n = confirmState.ids.length;
      const label = n === 1 ? 'package' : `${n} packages`;
      const failNote =
        failures > 0
          ? ` ${failures} provisioning operation${failures === 1 ? '' : 's'} failed and will be retried automatically (you were emailed).`
          : '';
      // 04 #117: the operator summary email has always carried this line; the banner never did, so
      // the stamped rows were invisible on screen (04 #112 rule 5's operator-visibility half).
      const stampedNote =
        alreadySuspended > 0
          ? ` ${alreadySuspended} assignment${alreadySuspended === 1 ? ' was' : 's were'} already suspended for non-payment and stayed that way; ${alreadySuspended === 1 ? 'it is' : 'they are'} recorded in this batch and re-enabling will leave ${alreadySuspended === 1 ? 'it' : 'them'} suspended until the balance is paid.`
          : '';
      // 04 #116: name the two populations separately. Restoring a package does not settle a debt.
      const heldNote =
        heldByDebt > 0
          ? ` ${heldByDebt} client${heldByDebt === 1 ? ' stays' : 's stay'} suspended until their past due balance is paid.`
          : '';
      setInfo(
        confirmState.kind === 'disable'
          ? `Disabled the ${label}. ${clients} client${clients === 1 ? ' was' : 's were'} suspended and notified.${stampedNote}${failNote}`
          : cleanSlate
            ? `Re-enabled the ${label} with a clean slate. ${clients} client${clients === 1 ? ' stays' : 's stay'} suspended.`
            : `Re-enabled the ${label}. ${restored} client${restored === 1 ? ' was' : 's were'} restored and notified.${heldNote}${failNote}`,
      );
      if (confirmState.fromBulk) clear();
      setConfirmState(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  // Totals for the confirm dialogs (summed across the selected packages). NOTE: must sit BELOW the
  // confirmState declaration — these consts evaluate during render (TDZ), unlike the callbacks above.
  const confirmClientTotal =
    confirmState?.pre.reduce(
      (acc, p) =>
        acc + (confirmState.kind === 'disable' ? p.affectedClientCount : (p.pendingBatch?.clientCount ?? 0)),
      0,
    ) ?? 0;
  // 04 #116: what the restore will ACTUALLY do, from the server's own predicate. The older
  // `clientCount` is the batch size, which counts the units stamped in tag-only while
  // non-payment-suspended; promising to restore and notify those was the defect.
  const confirmRestorableTotal =
    confirmState?.pre.reduce(
      (acc, p) => acc + (p.pendingBatch?.restorableClientCount ?? p.pendingBatch?.clientCount ?? 0),
      0,
    ) ?? 0;
  const confirmHeldTotal =
    confirmState?.pre.reduce((acc, p) => acc + (p.pendingBatch?.heldByDebtClientCount ?? 0), 0) ?? 0;
  const confirmDisabledDate = confirmState?.pre.find((p) => p.pendingBatch)?.pendingBatch?.at ?? null;
  const confirmLabelText = confirmState
    ? confirmState.ids.length === 1
      ? `“${confirmState.pre[0]?.package.name ?? confirmState.ids[0]}”`
      : `${confirmState.ids.length} packages`
    : '';

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Service Packages</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {packages?.length ?? 0} package{(packages?.length ?? 0) === 1 ? '' : 's'} · includes inactive
          </p>
        </div>
        <Button onClick={openCreate}>New package</Button>
      </header>

      {formOpen ? (
        <div ref={formRef}>
          <Card>
            <form onSubmit={submit} className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl">{editingId ? 'Edit package' : 'New package'}</h2>
                {editingId ? <span className="text-xs text-[var(--color-muted)] font-mono">{editingId}</span> : null}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Name (internal)" required>
                  <Input value={form.name} onChange={(v) => update({ name: v })} placeholder="Microsoft 365 Essentials Bundle" required />
                </Field>
                <Field label="Short name" required>
                  <Input value={form.shortName} onChange={(v) => update({ shortName: v })} placeholder="M365 Essentials" required />
                </Field>
                <Field label="Client-facing name (Blank = Uses Name)">
                  <Input value={form.clientFacingName} onChange={(v) => update({ clientFacingName: v })} placeholder={form.name || 'Shown to clients'} />
                </Field>
              </div>

              <Field label="Description" required>
                <textarea
                  value={form.description}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={2}
                  required
                  className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                />
              </Field>

              <div className="grid gap-4 md:grid-cols-3">
                <Field
                  label="Logo URL (optional)"
                  tip="Paste a hosted image URL, upload an image below, or both. An uploaded image takes precedence; the URL is kept as the fallback, so removing the upload brings it back. With neither, tiles show the package's initial letter."
                >
                  <Input value={form.logoUrl} onChange={(v) => update({ logoUrl: v })} placeholder="https://…/logo.svg" />
                </Field>
                <Field label="Category" required>
                  <Input value={form.category} onChange={(v) => update({ category: v })} placeholder="bundles" required list="pkg-categories" />
                </Field>
                <Field label="Sub-category" required>
                  <Input value={form.subCategory} onChange={(v) => update({ subCategory: v })} placeholder="productivity" required list="pkg-subcategories" />
                </Field>
                <datalist id="pkg-categories">
                  {categoryOptions.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <datalist id="pkg-subcategories">
                  {subCategoryOptions.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>

              {editingId ? (
                <div className="flex items-center gap-4 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4">
                  <AvatarImage
                    src={
                      editLogoRef
                        ? `/api/package-logo?packageId=${encodeURIComponent(editingId)}&v=${logoVersion}`
                        : form.logoUrl.trim() || null
                    }
                    name={form.name || editingId}
                    size={44}
                    shape="square"
                    alt="Package logo preview"
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-medium">
                      Logo image
                      <InfoTip text="Upload an image file for this package's logo. It is stored by the platform and shown everywhere in place of the Logo URL. The URL stays saved, so removing the uploaded image falls back to it." />
                    </p>
                    <p className="text-xs text-[var(--color-muted)]">
                      {editLogoRef
                        ? 'Using the uploaded image. The Logo URL is kept as the fallback.'
                        : form.logoUrl.trim()
                          ? 'Using the Logo URL. Upload an image to take precedence over it.'
                          : 'No logo yet. Tiles show the initial letter until a URL or image is set.'}
                    </p>
                    {logoError ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">{logoError}</p> : null}
                  </div>
                  <label className="cursor-pointer rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-1.5 text-sm hover:bg-[rgb(var(--edge-rgb)/0.03)]">
                    {logoBusy ? 'Working…' : editLogoRef ? 'Replace image' : 'Upload image'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={logoBusy}
                      onChange={(e) => {
                        void onPackageLogoFile(e.target.files?.[0] ?? null);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editLogoRef ? (
                    <Button type="button" onClick={() => void removeUploadedLogo()} disabled={logoBusy}>
                      Remove image
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {!editingId ? (
                <Field label="Package ID (optional, derived from name if blank)">
                  <Input value={form.id} onChange={(v) => update({ id: v })} placeholder="pkg-m365-essentials" mono />
                </Field>
              ) : null}

              <Field label="Billing entity" required>
                <Select value={form.defaultBillingEntity} onChange={(v) => update({ defaultBillingEntity: v })} options={billingOptions} />
              </Field>

              {/* Member services — chosen at create, FIXED after (invariant #10). */}
              <div className="space-y-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4">
                <p className="text-sm font-medium">
                  Included services {editingId ? '(fixed after creation)' : <span className="text-[var(--tint-red-text)]">*</span>}
                </p>
                {editingId ? (
                  <>
                    {form.serviceIds.length === 0 ? (
                      <p className="text-sm text-[var(--color-muted)]">No services.</p>
                    ) : (
                      <ul className="flex flex-wrap gap-1.5">
                        {form.serviceIds.map((id) => (
                          <li key={id} className="rounded-full bg-[rgb(var(--edge-rgb)/0.05)] px-2.5 py-0.5 text-xs">
                            {serviceName(id)}
                            {servicesById.get(id) && !servicesById.get(id)!.active ? (
                              <span className="text-[var(--color-muted)]"> · inactive</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-[var(--color-muted)]">
                      A package's contents can't be changed after creation. To change the bundle, create a new package and
                      switch clients to it.
                    </p>
                  </>
                ) : activeServices.length === 0 ? (
                  <p className="text-sm text-[var(--color-muted)]">No active services to include. Create services first.</p>
                ) : (
                  <>
                    <div className="grid gap-1.5 sm:grid-cols-2 max-h-72 overflow-y-auto pr-1">
                      {activeServices.map((s) => {
                        const checked = form.serviceIds.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={[
                              'flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer',
                              checked ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5' : 'border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)]',
                            ].join(' ')}
                          >
                            <input type="checkbox" checked={checked} onChange={() => toggleMember(s.id)} className="mt-1" />
                            <span className="min-w-0">
                              <span className="block font-medium truncate">{s.name}</span>
                              <span className="block text-xs text-[var(--color-muted)]">
                                {money(s.defaultClientPrice)} · cost {money(s.defaultCostToYou)}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-[var(--color-muted)]">
                      {form.serviceIds.length} of {activeServices.length} selected · summed member cost {money(selectedCostSum)}.
                    </p>
                  </>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Client price ($)" required={!marginOnForEntity}>
                  <Input
                    value={form.defaultPrice}
                    onChange={(v) => update({ defaultPrice: v })}
                    type="number"
                    placeholder={marginOnForEntity ? 'blank = auto from margin' : 'required (margin off)'}
                  />
                </Field>
                <Field label="Currency">
                  <Input value={form.currency} onChange={(v) => update({ currency: v })} />
                </Field>
                <Field label="Sort order">
                  <Input value={form.sortOrder} onChange={(v) => update({ sortOrder: v })} type="number" />
                </Field>
              </div>
              <p className="-mt-2 text-xs text-[var(--color-muted)]">
                {marginOnForEntity
                  ? `Leave blank to auto-price from ${selectedBiz ? brandDisplayName(selectedBiz.name) : 'this billing entity'}'s margin off the summed member cost (${money(selectedCostSum)}), or enter a price to set it manually. Enter 0 for a free package.`
                  : `Margin is off for ${selectedBiz ? brandDisplayName(selectedBiz.name) : 'this billing entity'}: enter a price (0 for a free package). Turn margin on in Settings to auto-price.`}
              </p>

              {formError ? (
                <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">{formError}</div>
              ) : null}

              <div className="flex gap-3 pt-1">
                <Button type="submit" loading={submitting} disabled={submitting}>
                  {editingId ? 'Save changes' : 'Create package'}
                </Button>
                <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}
      {info ? (
        <p className="rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] px-3 py-2 text-sm text-[var(--tint-green-text)]">{info}</p>
      ) : null}
      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {packages && packages.length === 0 && !loading ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            No packages yet. Click <span className="font-medium">New package</span> to create the first one.
          </p>
        </Card>
      ) : null}

      {packages && packages.length > 0 ? (
        <div className="space-y-3">
          {/* filter toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search name or ID…"
              className="min-w-[12rem] rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            />
            <select
              value={catFilter}
              onChange={(e) => {
                const nc = e.target.value;
                setCatFilter(nc);
                setPage(0);
                if (subFilter && nc && !(packages ?? []).some((p) => p.category === nc && p.subCategory === subFilter)) {
                  setSubFilter('');
                }
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">All categories</option>
              {filterCatOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={subFilter}
              onChange={(e) => {
                const ns = e.target.value;
                setSubFilter(ns);
                setPage(0);
                if (catFilter && ns && !(packages ?? []).some((p) => p.subCategory === ns && p.category === catFilter)) {
                  setCatFilter('');
                }
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">All sub-categories</option>
              {filterSubOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(0);
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <span className="text-xs text-[var(--color-muted)]">
              {filtered.length} of {packages.length}
            </span>
          </div>

          {/* bulk-action bar (selection persists across pages + filters) */}
          {selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/5 px-3 py-2 text-sm">
              <span className="font-medium">
                {selected.size} selected{selected.size > pageIds.filter((id) => isSelected(id)).length ? ' (incl. other pages/filters)' : ''}
              </span>
              <Button type="button" onClick={() => void bulkSetActive(true)} loading={bulkBusy} disabled={bulkBusy}>
                Activate
              </Button>
              <Button type="button" variant="ghost" onClick={() => void bulkSetActive(false)} disabled={bulkBusy}>
                Deactivate
              </Button>
              <button type="button" onClick={clear} className="text-xs underline text-[var(--color-muted)]">
                Clear
              </button>
            </div>
          ) : null}

          {filtered.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--color-muted)]">No packages match your filters.</p>
            </Card>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] glass-flat">
                <table className="w-full text-sm">
                  <thead className="bg-[rgb(var(--edge-rgb)/0.05)] text-left">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={(e) => setMany(pageIds, e.target.checked)}
                          aria-label="Select all on this page"
                        />
                      </th>
                      <th className="px-4 py-3 font-medium">Package</th>
                      <th className="px-4 py-3 font-medium">Includes</th>
                      <th className="px-4 py-3 font-medium">Price</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Active</th>
                      <th className="px-4 py-3 font-medium">Business</th>
                      <th className="px-4 py-3 font-medium">Clients</th>
                      <th className="px-4 py-3 font-medium text-right">Edit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
                    {pageItems.map((p) => (
                      <tr key={p.id} className={isSelected(p.id) ? 'bg-[var(--color-accent)]/5' : undefined}>
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected(p.id)}
                            onChange={() => toggle(p.id)}
                            aria-label={`Select ${p.name}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <AvatarImage src={(p.logoBlobRef ? `/api/package-logo?packageId=${encodeURIComponent(p.id)}` : p.logoUrl) || null} name={p.name} size={36} shape="square" alt={`${p.name} logo`} />
                            <div>
                              <div className="font-medium">{p.name}</div>
                              <div className="text-xs text-[var(--color-muted)]">
                                {p.shortName} · <span className="font-mono">{p.id}</span>
                              </div>
                              {p.clientFacingName ? (
                                <div className="text-xs text-[var(--color-muted)]">Shown to clients: {p.clientFacingName}</div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[var(--color-muted)]">
                          <span title={p.serviceIds.map(serviceName).join(', ')}>
                            {p.serviceIds.length} service{p.serviceIds.length === 1 ? '' : 's'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[var(--color-muted)]">
                          {money(p.defaultPrice)}
                          {p.pricingMode === 'margin' ? <span className="text-xs"> · margin</span> : null}
                        </td>
                        <td className="px-4 py-3">
                          {p.active ? (
                            <span className="inline-flex items-center rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-green-text)]">
                              Active
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center rounded-full bg-[rgb(var(--edge-rgb)/0.10)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted)]"
                              title={p.disabledAt ? `Disabled ${new Date(p.disabledAt).toLocaleString()}` : undefined}
                            >
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ActiveToggle active={p.active} busy={busyId === p.id} label={p.name} onToggle={() => toggleActive(p)} />
                        </td>
                        <td className="px-4 py-3 text-[var(--color-muted)]">
                          {brandDisplayName(businesses.find((b) => b.id === p.defaultBillingEntity)?.name ?? p.defaultBillingEntity)}
                        </td>
                        <td className="px-4 py-3 text-[var(--color-muted)]">
                          {assignedCounts[p.id] ?? 0} client{(assignedCounts[p.id] ?? 0) === 1 ? '' : 's'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <PillButton size="lg" onClick={() => openEdit(p)} disabled={busyId === p.id}>
                              Edit
                            </PillButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--color-muted)]">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
                      Prev
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmState?.kind === 'disable'}
        title={`Disable ${confirmLabelText} for ALL clients?`}
        confirmLabel="Disable"
        cancelLabel="Keep active"
        confirmVariant="danger"
        busy={confirmBusy}
        onConfirm={() => void runConfirmed()}
        onCancel={() => setConfirmState(null)}
      >
        <p>
          {confirmClientTotal === 0
            ? 'No clients currently hold an active assignment, so only the catalog entry is disabled (it is hidden from new assignment).'
            : `${confirmClientTotal} client${confirmClientTotal === 1 ? '' : 's'} currently hold${confirmClientTotal === 1 ? 's' : ''} this package and will be suspended: the package and its included services are paused, access is removed (delicensed), and each affected client is notified by email and an in-app notice.`}
        </p>
        <p className="text-[var(--color-muted)]">
          Billing proration for the disabled period is applied later by the invoicing engine (Stage 4); nothing
          is billed or refunded right now. Re-enabling will offer to restore the suspended clients.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmState?.kind === 'enable'}
        title={`Re-enable ${confirmLabelText}?`}
        confirmLabel={cleanSlate ? 'Re-enable (clean slate)' : 'Re-enable and restore'}
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={confirmBusy}
        onConfirm={() => void runConfirmed()}
        onCancel={() => setConfirmState(null)}
      >
        <p>
          {confirmClientTotal} client{confirmClientTotal === 1 ? ' was' : 's were'} suspended when this was
          disabled{confirmDisabledDate ? ` on ${new Date(confirmDisabledDate).toLocaleDateString()}` : ''}.{' '}
          {confirmRestorableTotal > 0
            ? `Restoring re-adds access for ${confirmRestorableTotal} of them (the package and its included services) and notifies them.`
            : 'Restoring will not bring any of them back.'}
        </p>
        {confirmHeldTotal > 0 ? (
          <p>
            {confirmHeldTotal === 1
              ? '1 of those clients has a past due balance and stays suspended.'
              : `${confirmHeldTotal} of those clients have a past due balance and stay suspended.`}{' '}
            Re-enabling the package does not settle a debt, so they are not re-added and not notified.
          </p>
        ) : null}
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={cleanSlate}
            onChange={(e) => setCleanSlate(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Re-enable with a clean slate: leave those clients suspended (they are not notified; they can be
            reactivated individually later).
          </span>
        </label>
      </ConfirmDialog>
    </div>
  );
}


function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  mono,
  list,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  mono?: boolean;
  list?: string;
}): JSX.Element {
  return (
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      list={list}
      step={type === 'number' ? 'any' : undefined}
      className={['w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]', mono ? 'font-mono text-xs' : ''].join(' ')}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
